import React from 'react';
import { Link } from 'react-router-dom';
import { format, formatDistanceToNowStrict, subDays } from 'date-fns';
import useSWR from 'swr';
import Tippy from '@tippyjs/react';
import { Button } from '@nearst/ui';
import { CheckIcon, ArrowRightIcon, ExclamationTriangleIcon } from '@heroicons/react/20/solid';

import { useShop } from '@services/ShopProvider';
import { useInsights } from '@services/insights';
import { getFeedQualityStats, getOrgFeedQualityStats } from '@services/stock';

import { OverviewCard } from '../../components/OverviewCard';
import { EmptyState } from '../../components/EmptyState';
import { CARD_STATES, useCardEmptyState } from '../../hooks/useCardEmptyState';
import { STALE_INGEST_THRESHOLD_DAYS, daysSince, isStaleIngest, deriveLocationHealth } from '../../lib/feed-health';

import styles from './InventoryConnectionCard.module.scss';

const DOT_COUNT = 30;

// "12 minutes ago" doesn't fit the narrow status row alongside the provider
// name and label. Compact common units to single letters: 12m, 3h, 5d, etc.
const compactAge = (date) => {
	if (!date) return null;
	const raw = formatDistanceToNowStrict(date, { addSuffix: false });
	return (
		raw
			.replace(/ seconds?$/, 's')
			.replace(/ minutes?$/, 'm')
			.replace(/ hours?$/, 'h')
			.replace(/ days?$/, 'd')
			.replace(/ weeks?$/, 'w')
			.replace(/ months?$/, 'mo')
			.replace(/ years?$/, 'y') + ' ago'
	);
};

/**
 * Build a 30-day status dot strip from real ingest history. In chain view the
 * rows are summed across every store in the org (one row per date), so a day's
 * dot is green if any store ingested that day; the hover shows the org-wide
 * upload count for that day. Same warehouse table the existing "Stock upload
 * history" graph on the Feed page uses. Counts arrive as strings from Postgres
 * SUM(), so coerce to Number before comparing/formatting.
 */
const buildDotSeries = (stockCountRows) => {
	const rowByDate = new Map((stockCountRows ?? []).map((r) => [r.date, r]));
	const today = new Date();
	return Array.from({ length: DOT_COUNT }, (_, i) => {
		const day = subDays(today, DOT_COUNT - 1 - i);
		const key = format(day, 'yyyy-MM-dd');
		const row = rowByDate.get(key);
		return {
			date: key,
			isActive: Number(row?.total_uploaded_barcodes ?? 0) > 0,
			totalUploaded: Number(row?.total_uploaded_barcodes ?? 0)
		};
	});
};

// Single-store hits the per-shop endpoint; chain hits the aggregate endpoint.
// Either way: one HTTP request, regardless of org size.
const EMPTY_TOTALS = { totalItems: 0, activeItems: 0, erroneousItems: 0 };

const fetchTotals = async (scopeShopIds) => {
	if (scopeShopIds.length === 0) return EMPTY_TOTALS;
	if (scopeShopIds.length === 1) {
		return (await getFeedQualityStats(scopeShopIds[0]).catch(() => null)) ?? EMPTY_TOTALS;
	}
	return (await getOrgFeedQualityStats().catch(() => null)) ?? EMPTY_TOTALS;
};

export const InventoryConnectionCard = () => {
	const { data: shops, shop } = useShop();
	const isScoped = Boolean(shop?.id);
	const scopeShops = isScoped ? [shop] : (shops ?? []);
	const scopeShopIds = scopeShops.map((s) => s.id);
	const primaryShop = scopeShops[0];

	// A shop can have separate sources for stock and product data — common when a
	// retailer ingests inventory from a POS over FTP while their PIM (Magento,
	// etc.) supplies the product metadata. Render both rows when both are set.
	// Each source kind shapes its `latestIngest` differently — stock uses
	// `.timestamp`, product uses `.customProductTimestamp`. Fall back to
	// `updatedAt` so we always have something even if the ingest shape changes.
	const latestIngestAt = (source) =>
		source?.latestIngest?.timestamp ?? source?.latestIngest?.customProductTimestamp ?? source?.updatedAt ?? null;
	const sources = [
		{ kind: 'Stock data', source: primaryShop?.stockInventorySource },
		{ kind: 'Product data', source: primaryShop?.productInventorySource }
	].filter((s) => s.source);
	const latestIngestDate = latestIngestAt(primaryShop?.stockInventorySource)
		? new Date(latestIngestAt(primaryShop.stockInventorySource))
		: null;

	const totalsKey = scopeShopIds.length ? ['inventory-totals', scopeShopIds.length === 1 ? scopeShopIds[0] : 'org'] : null;
	const { data: totals } = useSWR(totalsKey, () => fetchTotals(scopeShopIds));

	// Dot strip spans the whole scope: a single store when scoped, or every store
	// in the org otherwise. Summing across the shop ids and grouping by date
	// (handled server-side when `sum` + a `date` column are passed) collapses the
	// org down to one row per day, so the strip reflects all of Leyland's stores
	// rather than just the first one.
	const stockCountQuery = scopeShopIds.length
		? {
				id: scopeShopIds,
				table: 'stock_count',
				columns: ['total_uploaded_barcodes', 'date'],
				sum: ['total_uploaded_barcodes'],
				from: format(subDays(new Date(), DOT_COUNT - 1), 'yyyy-MM-dd'),
				to: format(new Date(), 'yyyy-MM-dd'),
				sort: [{ column: 'date', order: 'asc' }]
			}
		: null;
	const { resultSet: stockCountRows } = useInsights(stockCountQuery);

	const emptyState = useCardEmptyState({
		cardId: 'inventory',
		minDaysForData: 0,
		dataIsEmpty: false
	});

	if (emptyState.state !== CARD_STATES.DATA) {
		return (
			<OverviewCard>
				<h2 className={styles.title}>Feed health</h2>
				<EmptyState state={emptyState.state} copy={emptyState.copy} ctaLabel={emptyState.ctaLabel} ctaHref={emptyState.ctaHref} />
			</OverviewCard>
		);
	}

	const dots = buildDotSeries(stockCountRows);
	const locations = deriveLocationHealth(scopeShops);
	const scopedIngestAt = latestIngestAt(primaryShop?.stockInventorySource);
	const scopedStale = isScoped && isStaleIngest(scopedIngestAt);
	const staleLocations = locations.connected - locations.receiving;

	return (
		<OverviewCard>
			<h2 className={styles.title}>Feed health</h2>
			<div className={styles.sourceList}>
				{sources.map(({ kind, source }) => {
					const label = source?.providerLabel ?? source?.kind ?? kind;
					const at = latestIngestAt(source);
					const age = compactAge(at ? new Date(at) : null);
					const stale = isStaleIngest(at);
					return (
						<div key={kind} className={styles.statusRow}>
							<div className={stale ? styles.warnPill : styles.checkPill} aria-hidden="true">
								{stale ? (
									<ExclamationTriangleIcon className={styles.checkIcon} />
								) : (
									<CheckIcon className={styles.checkIcon} />
								)}
							</div>
							<span className={styles.providerName}>{label}</span>
							<span className={styles.muted}>{kind}</span>
							{age && <span className={`${styles.timeAgo} ${stale ? styles.timeAgoStale : ''}`}>{age}</span>}
						</div>
					);
				})}
			</div>

			<div className={styles.dotStrip}>
				{dots.map((day, i) => (
					<Tippy
						key={day.date ?? i}
						content={
							<div className={styles.dotTooltip}>
								<strong>{format(new Date(day.date), 'EEE d MMM')}</strong>
								{day.isActive ? <div>{day.totalUploaded.toLocaleString()} items uploaded</div> : <div>No ingest</div>}
							</div>
						}
						delay={[80, 0]}
					>
						<span className={`${styles.dot} ${day.isActive ? styles.dotActive : styles.dotInactive}`} />
					</Tippy>
				))}
			</div>
			<div className={styles.dotLabels}>
				<span>30 days</span>
				<span>Today</span>
			</div>

			<div className={styles.statusBlock}>
				<h3 className={styles.subheading}>Status</h3>
				<p className={styles.statusCopy}>
					{locations.connected === 0
						? 'Awaiting first stock update.'
						: isScoped
							? scopedStale
								? `No stock updates received for ${daysSince(scopedIngestAt)} days.`
								: 'Receiving stock updates from your POS.'
							: staleLocations > 0
								? `We are receiving stock updates for ${locations.receiving} of ${locations.connected} locations.`
								: `We are receiving stock updates for ${locations.connected} ${locations.connected === 1 ? 'location' : 'locations'}.`}
				</p>
				{!isScoped && staleLocations > 0 && (
					<p className={`${styles.statusCopy} ${styles.warningCopy}`}>
						{staleLocations} {staleLocations === 1 ? 'location has' : 'locations have'} not sent stock updates in the last{' '}
						{STALE_INGEST_THRESHOLD_DAYS} days.
					</p>
				)}
				{totals && totals.totalItems > 0 && (
					<p className={styles.statusCopy}>
						{/* Feed facts only — "active" is a visibility concept that belongs
						    to the Reach card (where it's GMC-backed for Google) and the
						    two definitions produce different numbers side by side. */}
						Your inventory has {totals.totalItems.toLocaleString()} items (
						{(totals.activeItems + totals.erroneousItems).toLocaleString()} in stock).
					</p>
				)}
			</div>

			<div className={styles.footer}>
				<Button as={Link} to="/inventory/items" className={styles.cta}>
					<span>Feed &amp; Inventory Health</span>
					<ArrowRightIcon className={styles.arrow} aria-hidden="true" />
				</Button>
			</div>
		</OverviewCard>
	);
};
